import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as intlTelInput from 'intl-tel-input';
import { AsYouType, parseIncompletePhoneNumber } from 'libphonenumber-js/min';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

declare const intlTelInputGlobals: any;
declare const intlTelInputUtils: any;

@Injectable({ providedIn: 'root' })
export class PhoneService {
  static sanitizePhoneInput(phoneNumber: string) {
    return parseIncompletePhoneNumber(phoneNumber || '');
  }

  static formatPhoneInput(phoneNumber: string, countryCode?: string | null) {
    if (!phoneNumber) {
      return '';
    }

    const normalizedCountryCode = countryCode?.toUpperCase();
    const formatter = phoneNumber.startsWith('+') ? new AsYouType() : normalizedCountryCode ? new AsYouType(normalizedCountryCode as any) : new AsYouType();

    return formatter.input(phoneNumber);
  }

  static detectCountryFromPhoneInput(phoneNumber: string) {
    if (!phoneNumber.startsWith('+')) {
      return null;
    }

    const formatter = new AsYouType();
    formatter.input(phoneNumber);

    return formatter.getCountry()?.toLowerCase() || null;
  }

  static isValidPhone(phoneNumber: string) {
    const phoneInput = PhoneService.createPhoneInput(phoneNumber);
    return !!phoneInput.getNumber() && phoneInput.isValidNumber();
  }

  static getFullPhoneNumberWithMask(phoneNumber: string) {
    return new Observable<string>(observer => {
      const phoneInput = PhoneService.createPhoneInput(phoneNumber);

      phoneInput.promise.then(() => {
        observer.next(typeof intlTelInputUtils !== 'undefined' ? phoneInput.getNumber(intlTelInputUtils.numberFormat.INTERNATIONAL) : phoneNumber);
      });
      phoneInput.promise.catch((error: any) => observer.error(error));
    }).pipe(take(1));
  }

  static createPhoneInput(phoneNumber: string) {
    const wrapperElement = document.createElement('div'); // Sim, dá erro se o input não tiver um parentElement
    const inputElement = document.createElement('input');
    inputElement.value = phoneNumber;
    wrapperElement.appendChild(inputElement);

    return intlTelInput(inputElement, {
      nationalMode: true,
      placeholderNumberType: 'MOBILE',
      utilsScript: this.intlTelUtilsScript()
    });
  }

  static intlTelUtilsScript() {
    return intlTelInputGlobals?.version && `https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/${intlTelInputGlobals.version}/js/utils.js`;
  }

  constructor(private httpClient: HttpClient) {}

  sendSmsVerification(phoneNumber: string, forceUserCreation?: boolean) {
    return this.sendPhoneVerification('sms', phoneNumber, forceUserCreation);
  }

  sendWhatsappVerification(phoneNumber: string, forceUserCreation?: boolean) {
    return this.sendPhoneVerification('wa', phoneNumber, forceUserCreation);
  }

  submitSmsVerificationCode(phoneNumber: string, verificationCode: string) {
    return this.submitPhoneVerificationCode('sms', phoneNumber, verificationCode);
  }

  submitWhatsappVerificationCode(phoneNumber: string, verificationCode: string) {
    return this.submitPhoneVerificationCode('wa', phoneNumber, verificationCode);
  }

  private sendPhoneVerification(type: 'sms' | 'wa', phoneNumber: string, forceUserCreation: boolean) {
    return this.httpClient.disableLoader().post(`/auth/login/${type}/send`, { phone_number: phoneNumber, create: forceUserCreation });
  }

  private submitPhoneVerificationCode(type: 'sms' | 'wa', phoneNumber: string, verificationCode: string) {
    return this.httpClient.disableLoader().post<{ access_token: string }>(`/auth/login/${type}/verify`, { phone_number: phoneNumber, code: verificationCode });
  }
}
